import React, { useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BRANCHES, BRANCH_LIST } from "@/lib/brand";

const REFERRAL_SOURCES = [
  "En regardant la TV",
  "En faisant une recherche sur Internet",
  "Par les réseaux sociaux",
  "Par le bouche-à-oreille",
  "Vous avez vu passer un de nos véhicules",
  "Vous avez une carte de visite",
];

export default function Contact() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialBranch = ["studio", "securite", "proprete"].includes(params.get("branch"))
    ? params.get("branch")
    : "studio";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    branch: initialBranch,
    service: "",
    referral: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const b = BRANCHES[form.branch];

  useEffect(() => {
    // reset service when branch changes
    setForm((f) => ({ ...f, service: "" }));
  }, [form.branch]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message || !form.service) {
      toast.error("Veuillez compléter les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/quotes", form);
      setSent(true);
      toast.success("Demande envoyée avec succès. Nous revenons vers vous sous 24h.");
      setForm((f) => ({
        name: "",
        email: "",
        phone: "",
        company: "",
        branch: f.branch,
        service: "",
        referral: "",
        message: "",
      }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erreur lors de l'envoi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="pt-32 pb-12 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-6">Contact & devis</div>
        <h1 className="font-display text-5xl sm:text-6xl md:text-7xl font-bold tracking-tighter max-w-4xl">
          Parlons de votre projet.
        </h1>
        <p className="mt-6 text-lg text-zinc-400 max-w-2xl">
          Sélectionnez le pôle concerné, décrivez votre besoin — nous revenons vers vous sous 24h.
        </p>
      </section>

      <section className="pb-24 max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-4 space-y-3" data-testid="contact-branch-selector">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Choix du pôle</div>
          {BRANCH_LIST.map((br) => {
            const active = form.branch === br.key;
            return (
              <button
                key={br.key}
                type="button"
                onClick={() => setForm((f) => ({ ...f, branch: br.key }))}
                data-testid={`select-branch-${br.key}`}
                className={`w-full text-left p-5 rounded-xl border bg-[#0e0e0e] transition-all ${
                  active ? `${br.accentBorder} ${br.glow}` : "border-white/10 hover:border-white/20"
                }`}
                style={active ? { boxShadow: `inset 0 0 0 1px ${br.accent}` } : {}}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display text-lg font-semibold">{br.name}</div>
                    <div className={`text-xs mt-1 ${br.accentText}`}>{br.tagline}</div>
                  </div>
                  {active && (
                    <span
                      className="w-7 h-7 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: br.accent }}
                    >
                      <Check size={14} className="text-white" />
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </aside>

        <form
          onSubmit={submit}
          className={`lg:col-span-8 rounded-xl border ${b.accentBorder} bg-[#0e0e0e] p-8 ${b.glow}`}
          data-testid="quote-form"
        >
          {sent ? (
            <div className="text-center py-16" data-testid="quote-success">
              <div
                className="w-16 h-16 rounded-full mx-auto flex items-center justify-center"
                style={{ backgroundColor: b.accentSoft, color: b.accent }}
              >
                <Check size={32} />
              </div>
              <h2 className="mt-6 font-display text-3xl font-semibold">Demande bien reçue !</h2>
              <p className="mt-3 text-zinc-400 max-w-md mx-auto">
                Merci pour votre demande. Notre équipe revient vers vous sous 24h ouvrées.
              </p>
              <div className="mt-8 flex gap-3 justify-center">
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  data-testid="quote-new-request"
                  className="px-5 py-2.5 rounded-md border border-white/20 hover:bg-white/5"
                >
                  Nouvelle demande
                </button>
                <Link to="/" className="px-5 py-2.5 rounded-md bg-white text-black font-semibold">
                  Retour à l'accueil
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-[0.2em]" style={{ color: b.accent }}>
                Demande pour {b.name}
              </div>
              <h2 className="font-display text-3xl mt-2 mb-8">Décrivez votre besoin</h2>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nom complet *" name="name" value={form.name} onChange={set("name")} />
                <Field label="Email *" name="email" type="email" value={form.email} onChange={set("email")} />
                <Field label="Téléphone" name="phone" value={form.phone} onChange={set("phone")} />
                <Field label="Entreprise" name="company" value={form.company} onChange={set("company")} />
              </div>

              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Service *</label>
                  <select
                    value={form.service}
                    onChange={set("service")}
                    data-testid="quote-service-select"
                    className="mt-2 w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3 text-white focus:outline-none focus:ring-2"
                    style={{ "--tw-ring-color": b.accent }}
                  >
                    <option value="">— Sélectionnez —</option>
                    {b.services.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Comment nous avez-vous connu ?</label>
                  <select
                    value={form.referral}
                    onChange={set("referral")}
                    data-testid="quote-referral-select"
                    className="mt-2 w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3 text-white focus:outline-none focus:ring-2"
                    style={{ "--tw-ring-color": b.accent }}
                  >
                    <option value="">— Optionnel —</option>
                    {REFERRAL_SOURCES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Votre message *</label>
                <textarea
                  rows={6}
                  value={form.message}
                  onChange={set("message")}
                  data-testid="quote-message"
                  placeholder="Décrivez votre projet, vos contraintes et vos objectifs…"
                  className="mt-2 w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3 text-white focus:outline-none focus:ring-2 resize-y"
                  style={{ "--tw-ring-color": b.accent }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="quote-submit"
                className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md font-semibold disabled:opacity-60"
                style={{ backgroundColor: b.accent, color: "#fff" }}
              >
                {loading && <Loader2 className="animate-spin" size={16} />}
                Envoyer ma demande
              </button>
              <p className="mt-4 text-xs text-zinc-500">
                * Champs obligatoires. Vos données ne sont utilisées que pour répondre à votre demande.
              </p>
            </>
          )}
        </form>
      </section>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        data-testid={`quote-input-${name}`}
        className="mt-2 w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-white/30"
      />
    </div>
  );
}
